import React, { useEffect, useState } from 'react';
import apiService from '../services/api';
import { PhaseReference, PhaseReferenceRequest } from '../types';
import RichTextEditor from '../components/RichTextEditor';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  AdjustmentsHorizontalIcon,
  ExclamationTriangleIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

/* ────────────────────────────────────────────────────────────────
   Helpers
──────────────────────────────────────────────────────────────── */
const SCORE_COLORS = ['bg-violet-500', 'bg-blue-500', 'bg-cyan-500', 'bg-emerald-500', 'bg-amber-500', 'bg-orange-500'];

function phaseBadgeColor(idx: number) {
  return SCORE_COLORS[idx % SCORE_COLORS.length];
}

function totalPoids(phases: PhaseReference[]) {
  return phases.reduce((s, p) => s + p.poids, 0);
}

function countLiItems(html: string | null | undefined): number {
  if (!html) return 0;
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.querySelectorAll('li').length;
}

function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

function deriveCode(nom: string): string {
  return nom.replace(/[^a-zA-Z]/g, '').substring(0, 4).toUpperCase() || 'CODE';
}

/* ────────────────────────────────────────────────────────────────
   Empty form
──────────────────────────────────────────────────────────────── */
const emptyForm = (): Omit<PhaseReferenceRequest, 'code'> & { code: string } => ({
  code: '',
  nom: '',
  poids: 10,
  cumul: 0,
  objectif: '',
  activitesCles: '',
  livrables: '',
  ordre: 1,
});

/* ════════════════════════════════════════════════════════════════
   Page
════════════════════════════════════════════════════════════════ */
const AdminPhases: React.FC = () => {
  const [phases, setPhases] = useState<PhaseReference[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editingPhase, setEditingPhase] = useState<PhaseReference | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<PhaseReference | null>(null);
  const [deleting, setDeleting] = useState(false);

  /* ── Load ── */
  const load = async () => {
    try {
      const data = await apiService.getPhaseReferences();
      setPhases(data);
    } catch {
      setError('Impossible de charger les phases');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  /* ── Flash success ── */
  const flash = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  /* ── Open create modal ── */
  const openCreate = () => {
    setEditingPhase(null);
    const nextOrdre = phases.length > 0 ? Math.max(...phases.map(p => p.ordre)) + 1 : 1;
    setForm({ ...emptyForm(), ordre: nextOrdre });
    setFormError(null);
    setShowModal(true);
  };

  /* ── Open edit modal ── */
  const openEdit = (phase: PhaseReference) => {
    setEditingPhase(phase);
    setForm({
      code: phase.code,
      nom: phase.nom,
      poids: phase.poids,
      cumul: phase.cumul,
      objectif: phase.objectif || '',
      activitesCles: phase.activitesCles || '',
      livrables: phase.livrables || '',
      ordre: phase.ordre,
    });
    setFormError(null);
    setShowModal(true);
  };

  /* ── Submit ── */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const totalExisting = phases
      .filter(p => !editingPhase || p.id !== editingPhase.id)
      .reduce((s, p) => s + p.poids, 0);
    if (totalExisting + form.poids > 100) {
      setFormError(`Le poids total dépasse 100% (actuellement ${totalExisting}% + ${form.poids}% = ${totalExisting + form.poids}%)`);
      return;
    }

    const payload: PhaseReferenceRequest = {
      code: editingPhase ? editingPhase.code : deriveCode(form.nom),
      nom: form.nom,
      poids: form.poids,
      cumul: form.cumul,
      objectif: form.objectif,
      activitesCles: form.activitesCles,
      livrables: form.livrables,
      ordre: form.ordre,
    };

    setSaving(true);
    try {
      if (editingPhase) {
        await apiService.updatePhaseReference(editingPhase.id, payload);
        flash('Phase mise à jour avec succès');
      } else {
        await apiService.createPhaseReference(payload);
        flash('Phase créée avec succès');
      }
      setShowModal(false);
      await load();
    } catch (err: any) {
      setFormError(err?.response?.data?.message || 'Une erreur est survenue');
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ── */
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiService.deletePhaseReference(deleteTarget.id);
      flash('Phase supprimée');
      setDeleteTarget(null);
      await load();
    } catch {
      setError('Impossible de supprimer cette phase');
    } finally {
      setDeleting(false);
    }
  };

  /* ── Loading ── */
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="w-10 h-10 rounded-full border-[3px] border-violet-100 border-t-violet-600 animate-spin" />
        <p className="text-sm text-gray-400">Chargement des phases…</p>
      </div>
    );
  }

  const total = totalPoids(phases);

  return (
    <div className="space-y-6 pb-10">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Référentiel des phases</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Définissez les phases du cycle de développement, leurs poids et leurs activités clés
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white
            bg-violet-600 hover:bg-violet-700 rounded-xl shadow-sm transition-colors flex-shrink-0"
        >
          <PlusIcon className="h-4 w-4" />
          Nouvelle phase
        </button>
      </div>

      {/* ── Alerts ── */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}
      {successMsg && (
        <div className="flex items-center gap-3 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700">
          <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
          {successMsg}
        </div>
      )}

      {/* ── Poids summary ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-gray-700">Répartition des poids</span>
          <span className={`text-sm font-bold ${total === 100 ? 'text-emerald-600' : total > 100 ? 'text-red-600' : 'text-amber-600'}`}>
            {total} / 100%
            {total === 100 && ' ✓'}
            {total > 100 && ' — dépassement !'}
            {total < 100 && ` — reste ${100 - total}%`}
          </span>
        </div>
        <div className="flex h-5 rounded-full overflow-hidden gap-0.5">
          {phases.map((p, i) => (
            <div
              key={p.id}
              className={`${phaseBadgeColor(i)} flex items-center justify-center text-[9px] text-white font-bold transition-all duration-300`}
              style={{ width: `${p.poids}%` }}
              title={`${p.code} — ${p.poids}%`}
            >
              {p.poids >= 8 ? p.code : ''}
            </div>
          ))}
          {total < 100 && (
            <div
              className="bg-gray-100 flex items-center justify-center text-[9px] text-gray-400"
              style={{ width: `${100 - total}%` }}
            >
              {100 - total >= 8 ? `+${100 - total}%` : ''}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-3 mt-3">
          {phases.map((p, i) => (
            <div key={p.id} className="flex items-center gap-1.5 text-xs text-gray-600">
              <span className={`w-2.5 h-2.5 rounded-sm ${phaseBadgeColor(i)}`} />
              <span className="font-medium">{p.code}</span>
              <span className="text-gray-400">{p.poids}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Table ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
          <AdjustmentsHorizontalIcon className="h-5 w-5 text-violet-500" />
          <h2 className="text-sm font-bold text-gray-900">{phases.length} phase{phases.length !== 1 ? 's' : ''} configurée{phases.length !== 1 ? 's' : ''}</h2>
        </div>

        {phases.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-14 h-14 bg-violet-50 rounded-2xl flex items-center justify-center">
              <AdjustmentsHorizontalIcon className="h-7 w-7 text-violet-400" />
            </div>
            <p className="text-sm font-medium text-gray-600">Aucune phase configurée</p>
            <button onClick={openCreate} className="text-sm text-violet-600 hover:underline font-medium">
              Créer la première phase
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-10">N°</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Phase</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-16">Poids</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-16">Cumul</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Objectif</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-20">Activités</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-20">Livrables</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {phases.map((phase, idx) => (
                  <tr key={phase.id} className="hover:bg-violet-50/30 transition-colors group">
                    <td className="px-4 py-4 text-xs text-gray-400">{phase.ordre}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold text-white ${phaseBadgeColor(idx)}`}>
                          {phase.code}
                        </span>
                        <span className="text-sm font-semibold text-gray-800">{phase.nom}</span>
                      </div>
                    </td>
                    <td className="px-3 py-4 text-center">
                      <span className="text-sm font-bold text-gray-700">{phase.poids}%</span>
                    </td>
                    <td className="px-3 py-4 text-center">
                      <span className="text-xs text-gray-500">{phase.cumul}%</span>
                    </td>
                    <td className="px-4 py-4 max-w-[240px]">
                      <p className="text-xs text-gray-600 line-clamp-2 leading-relaxed">
                        {stripHtml(phase.objectif) || '—'}
                      </p>
                    </td>
                    <td className="px-3 py-4 text-center">
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700">
                        {countLiItems(phase.activitesCles)}
                      </span>
                    </td>
                    <td className="px-3 py-4 text-center">
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                        {countLiItems(phase.livrables)}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => openEdit(phase)}
                          className="p-1.5 text-gray-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
                          title="Modifier"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(phase)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Supprimer"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ════════════════════════════════════════
          Create / Edit Modal
      ════════════════════════════════════════ */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm px-4 py-6 overflow-y-auto">
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl my-auto overflow-hidden">

            {/* Modal header */}
            <div className="bg-gradient-to-r from-violet-600 to-violet-700 px-6 py-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-white">
                    {editingPhase
                      ? <span className="flex items-center gap-2">
                          Modifier la phase
                          <span className="inline-flex px-2 py-0.5 rounded text-[11px] font-bold bg-white/20 text-white">
                            {editingPhase.code}
                          </span>
                        </span>
                      : 'Nouvelle phase'}
                  </h3>
                  <p className="text-xs text-violet-200 mt-0.5">
                    Total poids actuel : {totalPoids(phases.filter(p => !editingPhase || p.id !== editingPhase.id))}% + {form.poids}% = {totalPoids(phases.filter(p => !editingPhase || p.id !== editingPhase.id)) + form.poids}%
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                >
                  <XMarkIcon className="h-4 w-4 text-white" />
                </button>
              </div>
            </div>

            {/* Modal body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {formError && (
                <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                  <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                  {formError}
                </div>
              )}

              {/* Nom */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Nom complet <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={form.nom}
                  onChange={e => setForm({ ...form, nom: e.target.value })}
                  placeholder="Ex: INITIATION"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm
                    focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none"
                />
              </div>

              {/* Poids + Cumul + Ordre */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Poids (%) <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="number"
                    required
                    min={1}
                    max={100}
                    value={form.poids}
                    onChange={e => setForm({ ...form, poids: Number(e.target.value) })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm
                      focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">Contribution au score total</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Cumul (%) <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="number"
                    required
                    min={0}
                    max={100}
                    value={form.cumul}
                    onChange={e => setForm({ ...form, cumul: Number(e.target.value) })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm
                      focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">Cumul depuis le début</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Ordre <span className="text-red-400">*</span>
                  </label>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, ordre: Math.max(1, form.ordre - 1) })}
                      className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <ArrowUpIcon className="h-3.5 w-3.5 text-gray-500" />
                    </button>
                    <input
                      type="number"
                      required
                      min={1}
                      value={form.ordre}
                      onChange={e => setForm({ ...form, ordre: Number(e.target.value) })}
                      className="flex-1 px-3 py-2.5 border border-gray-300 rounded-xl text-sm text-center
                        focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, ordre: form.ordre + 1 })}
                      className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <ArrowDownIcon className="h-3.5 w-3.5 text-gray-500" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Objectif */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Objectif</label>
                <RichTextEditor
                  value={form.objectif || ''}
                  onChange={html => setForm({ ...form, objectif: html })}
                  placeholder="Décrivez l'objectif principal de cette phase…"
                  minHeight={80}
                />
              </div>

              {/* Activités clés */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Activités clés
                  <span className="ml-2 text-[10px] font-normal text-gray-400">
                    ({countLiItems(form.activitesCles)} activité{countLiItems(form.activitesCles) !== 1 ? 's' : ''} — utilisez la liste à puces)
                  </span>
                </label>
                <RichTextEditor
                  value={form.activitesCles || ''}
                  onChange={html => setForm({ ...form, activitesCles: html })}
                  placeholder="Saisissez les activités clés sous forme de liste à puces…"
                  minHeight={120}
                />
              </div>

              {/* Livrables */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Livrables
                  <span className="ml-2 text-[10px] font-normal text-gray-400">
                    ({countLiItems(form.livrables)} livrable{countLiItems(form.livrables) !== 1 ? 's' : ''} — utilisez la liste à puces)
                  </span>
                </label>
                <RichTextEditor
                  value={form.livrables || ''}
                  onChange={html => setForm({ ...form, livrables: html })}
                  placeholder="Saisissez les livrables sous forme de liste à puces…"
                  minHeight={120}
                />
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2.5 text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700
                    rounded-xl shadow-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saving && <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                  {editingPhase ? 'Enregistrer les modifications' : 'Créer la phase'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════
          Delete confirm dialog
      ════════════════════════════════════════ */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-900">Supprimer la phase</h3>
                <p className="text-xs text-gray-500 mt-0.5">Cette action est irréversible</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-5">
              Êtes-vous sûr de vouloir supprimer la phase{' '}
              <span className="font-bold text-gray-900">{deleteTarget.code} — {deleteTarget.nom}</span> ?
              <br />
              <span className="text-xs text-amber-600 mt-1 block">
                Attention : les scores associés à cette phase dans toutes les procédures seront perdus.
              </span>
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors
                  disabled:opacity-60 flex items-center gap-2"
              >
                {deleting && <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPhases;
